/* eslint-disable @typescript-eslint/no-unused-vars */
import StatefulSettings, { STATE_KEY_POSITIONS, STATE_KEY_REPORTCOMPLETE } from './StatefulSettings';
import { AuditActionTypes, AvailabilityDayType, EventType, ItemTypes, LockCountType, LockTypes, PenaltyPointConversionType, TraceLevel } from '../ts/enums';
import {
    ControlRecord, ITaskItem, IOwnerProfileSession, IReward, IPositionBasketInfo, ISessionSettings,
    ISystemConfig, IUserRecord, IRollResults, ISettingsItem, PilloryTopic, IControl, ITaskTimerInfo, IPenaltyTimerInfo, AuditMessage,
    IBasket, PenaltyBasketItem, TaskBasketItem, ScheduleBasketItem, BaseBasketItem, ITaskVoteConfig, IShoppingRequest, ITypingStats,
    IPenaltyItem,
    IEventItem
} from '../ts/Definitions';
import ConfigCache from '../utilities/ConfigCache';
import Settings from './settings';
import { convertToCountTypeString, isAvailableItem } from '../utilities/utils';
import { v1 } from 'uuid';
import { formatValidDate, convertToMinutes } from '../utilities/dateUtils';

let configCache: ConfigCache;

export const initialiseMessaging = (config: ConfigCache) => {
    configCache = config;
}

let muteAllMessages = false;

const formatMessageList = (items: string[]): string => items.filter(Boolean).map((item) => `• ${item}`).join('\n');

export const messageRequiresInteraction = (type?: MessageType) => {
    if (type === undefined) return false;
    switch (type) {
        case MessageType.ProofLines:
        case MessageType.RequestedTaskUpdateByMember:
        case MessageType.SessionMessage:
        case MessageType.ItemPostponeRequest:
        case MessageType.ReviewAvailability:
        case MessageType.RequestedDeleteTaskByMember:
        case MessageType.RequestedRemoveTaskByMember:
        case MessageType.RequestedTaskByMember:
        case MessageType.RequestedTaskInfoByMember:
        case MessageType.PermissionRequest:
        case MessageType.WeeklyTargetProofReview:
        case MessageType.PerkReward:
        case MessageType.OwnershipRequest:
        case MessageType.ShoppingRequest:
            return true;
    }
    return false;
}

export const isAddTodoMessageType = (type?: MessageType) => {
    if (type === undefined) return false;
    switch (type) {
        case MessageType.SessionMessage:
        case MessageType.RequestedTaskInfoByMember:
        case MessageType.DailyScore:
        case MessageType.MemberMessage:
        case MessageType.FailedItem:
        case MessageType.ProfileChosen:
        case MessageType.OwnerSelected:
        case MessageType.OwnershipRequest:
        case MessageType.ShoppingRequest:
        case MessageType.PreviewDay:
            return true;
    }
    return false;
}

export enum MessageType {
    DailyScore, MemberMessage, FailedItem,
    /**
     * @deprecated this enum value should not be used
     */
    SentMessages,
    ProofRequest,
    PurchaseRequest, Owned, ProofLines, SettingsChanged,
    WorkItemChanged, Rules, UserNameChanged,
    NewItemCreated, Unowned, UnknownPrompt, OwnershipRequest,
    OwnerSelected, ProfileChosen, Help, WorkingHomeNotification,
    WorkingDaysChanged, WelcomeMessage, PerkReward,
    HolidayModeChanged, NewSettings,
    /**
     * @deprecated this enum value should not be used
     */
    SessionMessage,
    PermissionRequest,
    AddedToPillory, PilloryResult, AddedByRoulette,
    /*state payload*/ RequestedTaskByMember,
    /*state payload*/ ItemPostponeRequest,
    PreviewDay,
    ReviewAvailability,
    RequestedDeleteTaskByMember,
    RequestedRemoveTaskByMember,
    RequestedTaskInfoByMember,
    RequestedTaskUpdateByMember,
    EventsNotToMiss,
    UnownedTaskAllocated,
    RequestedTaskVoteUpdateByMember,
    MissedAttendanceRequest,
    ShoppingRequest,
    GameResult,
    WeeklyTargetProofReview,
    MorningReadiness,
}


export enum NotificationType {
    General,
    VotedItem,
    OnlineMember,
    PreviewTomorrow,
    RequiredEvents,
    MessageSent,
    ScheduleInfo,
    ToyInfo,
    ErrorInfo,
    PilloryInfo,
    ProfileInfo,
    SettingsInfo,
    /**
     * @deprecated this enum value should not be used
     */
    SocialComment,
    PositionComplete,
}


export function MutableNotificationTypes(isOwner: boolean): INotificationTypeDescriptor[] {
    const common = [
        { type: NotificationType.General, name: 'System Messages', category: 'General', description: 'Important and useful messages from the site to notify you of updates and errors' },
        { type: NotificationType.OnlineMember, name: 'Member Online', category: 'Members', description: 'A member linked to you logs on to the site' },
        { type: NotificationType.ProfileInfo, name: 'Profile Info', category: 'Members', description: 'Profile updates have been made and successfully saved' },
        { type: NotificationType.SettingsInfo, name: 'Settings Info', category: 'Settings', description: 'Settings have been updated and applied to the session' },
        { type: NotificationType.ToyInfo, name: 'Toy Info', category: 'Settings', description: 'Information or errors about toys and their usage. Lovense related info' },
        // { type: NotificationType.ErrorInfo, name: 'Error Info', category: 'General', description: 'Errors across the system' },
        { type: NotificationType.MessageSent, name: 'Message Sent', category: 'General', description: 'Confirmation that a request has been sent to owners, members or system' },
        { type: NotificationType.ScheduleInfo, name: 'Schedule Info', category: 'Schedules', description: 'Updates to the schedule have been made and will affect the tasks and the times' },
    ];
    if (isOwner) return [...common];
    return [...common,
    { type: NotificationType.VotedItem, name: 'Vote on your task', category: 'Schedules', description: 'Get a popup whenever a vote is made on a task that is running' },
    { type: NotificationType.PreviewTomorrow, name: 'Preview  Tomorrow', category: 'Schedules', description: 'Preview tasks for tomorrow have been calculated and the notification lists the items required' },
    { type: NotificationType.RequiredEvents, name: 'Required Events', category: 'Schedules', description: 'Events for today have been calculated and some require you to be logged on to avoid penalties' },
    { type: NotificationType.PilloryInfo, name: 'Pillory Updates', category: 'Schedules', description: 'Pillory items have been updated by votes or you have been added to pillory' },
    { type: NotificationType.PilloryInfo, name: 'Position Complete', category: 'Schedules', description: 'When a held position is completed and a sound is played' },
    ];
}

export interface INotificationTypeDescriptor { type: NotificationType, name: string, description: string, category: string }
export interface IMessageTypeDescriptor { type: MessageType, name: string, description: string, category: string }

export function MutableMessageTypes(isOwner: boolean): IMessageTypeDescriptor[] {
    if (isOwner) return [
        { type: MessageType.DailyScore, name: 'Daily Score Report', category: 'Reports', description: 'Each morning the members report for their last day completed is sent to you with details of tasks completed and tasks missed and steps you can take.' },
        { type: MessageType.FailedItem, name: 'Failed Task Report', category: 'Events', description: 'Every time the member clicks failed task on their session when they are unable to complete a task you set, you will be notified.' },
        { type: MessageType.ProofRequest, name: 'Proof Requested', category: 'Proofs', description: 'Every time a member is requested to provide proof, owners are copied in to the message so they too see the request and the code the member can show to prove it is them.' },
        { type: MessageType.ProofLines, name: 'Lines Completed', category: 'Proofs', description: 'When a member completes lines, we show the results of their lines with the ability to make them redo them.' },
        { type: MessageType.WorkingHomeNotification, name: 'Free Day', category: 'Diary', description: 'When a member logs on in the morning and they are free for all tasks that day and it is not the weekend you will be notified.' },
        { type: MessageType.WorkingDaysChanged, name: 'Free Days Changed', category: 'Diary', description: 'When a member updates the days they are busy/free/unavailable you can be notified.' },
        { type: MessageType.PermissionRequest, name: 'Reward Request', category: 'Requests', description: 'When members earn enough to request a reward and then click to redeem their reward you will be notified.' },
        { type: MessageType.AddedToPillory, name: 'Pillory Added', category: 'Events', description: 'A message will notify you when your member has been sent to pillory by a task or event.' },
        { type: MessageType.PilloryResult, name: 'Pillory Completed', category: 'Events', description: 'A message will be sent when pillory has ended for the member and the task as a result has been added to the member session.' },
        { type: MessageType.PreviewDay, name: 'Tomorrow Preview Tasks', category: 'Reports', description: 'Members can set a preview for any day, so they can prepare their bags and items in advance if they are away or in an office. It will tell them what they need the following day. This is the list of items for the tasks they have been assigned.' },
        { type: MessageType.AddedByRoulette, name: 'Task Added By Roulette', category: 'Events', description: 'When roulette wheels complete, the task picked by the roulette task will be sent to you for your information.' },
        { type: MessageType.ItemPostponeRequest, name: 'Postpone Item Request', category: 'Requests', description: 'Members can request to postpone tasks they do not have time for in a day. A postpone adds it to the next day again for them still to do.' },
        { type: MessageType.RequestedTaskByMember, name: 'Task Request', category: 'Requests', description: 'Members can browse tasks in the system that are not available to them, they can requst it is added to their session too.' },
        { type: MessageType.ReviewAvailability, name: 'Task Availability Request', category: 'Requests', description: 'Members can request a task which is available for all days is reduced to be available only on free days, when they are alone. This is sometimes when NSFW.' },
        { type: MessageType.RequestedDeleteTaskByMember, name: 'Task Delete Request', category: 'Requests', description: 'Members can request tasks are removed from their settings, maybe it is a kink they are not happy with or a limit.' },
        { type: MessageType.RequestedRemoveTaskByMember, name: 'Task Remove Today Request', category: 'Requests', description: 'Members can request a task is removed from today for various reasons, this is not a postpone but a removal from the day only.' },
        { type: MessageType.RequestedTaskInfoByMember, name: 'Task Information Request', category: 'Requests', description: 'Some tasks may not be clear or need an owner to provide some decision or choice. This request allows the member to ask for information.' },
        { type: MessageType.RequestedTaskUpdateByMember, name: 'Task Update Request', category: 'Requests', description: 'Members can sometimes know when a task is out of date, maybe the option links no longer work or options are not right for the member.' },
        { type: MessageType.EventsNotToMiss, name: 'Events Due Today', category: 'Reports', description: 'This report will outline what events take place each day at the precise times they need to log on to view them without a penalty. These are only the events that would give a penalty if missed.' },
        { type: MessageType.RequestedTaskVoteUpdateByMember, name: 'Task Vote Reduce Request', category: 'Requests', description: 'This request is for when a member has a task that is running a long time in a vote and they want to plead to shorten it and ask you for mercy.' },
        { type: MessageType.GameResult, name: 'Game Result', category: 'Events', description: 'This message will be sent when a game has concluded and the results are available.' },
        { type: MessageType.WeeklyTargetProofReview, name: 'Weekly Target Proof Review', category: 'Proofs', description: 'A weekly goal submission has been sent with image proof and achieved value so you can approve or apply the configured penalty.' },
    ];
    return [
        { type: MessageType.MissedAttendanceRequest, name: 'Owner Requests for Attendance', category: 'Requests', description: 'When owners request presence and it is not met, this message will inform you what you have missed' },
        { type: MessageType.PreviewDay, name: 'Tomorrow Preview Tasks', category: 'Reports', description: 'Members can set a preview for any day, so they can prepare their bags and items in advance if they are away or in an office. It will tell them what they need the following day. This is the list of items for the tasks they have been assigned.' },
        { type: MessageType.EventsNotToMiss, name: 'Events Due Today', category: 'Reports', description: 'This report will outline what events take place each day at the precise times they need to log on to view them without a penalty. These are only the events that would give a penalty if missed.' },
        { type: MessageType.ProofRequest, name: 'Proof Request', category: 'Requests', description: 'An item when started has been requested to provide proof' },
        { type: MessageType.MorningReadiness, name: 'Tomorrow Morning Readiness', category: 'Reports', description: 'Each evening a reminder is sent with the time of tomorrow\'s first scheduled event so you can be awake and ready before it.' },
    ];
}


export const getUserScore = (userSettings: StatefulSettings, daysMissed: number | undefined,
    currentPenalties: number | undefined, intraday: boolean): number => {
    const basket = userSettings.getBasketItems(true);
    let finishedItems = basket.tasks.filter(c => userSettings.hasTaskFinished(c, intraday)).length;
    let inflightItems = basket.tasks.filter(c => !userSettings.hasTaskFinished(c, intraday) && !userSettings.shouldTaskRoll(c, intraday)).length;
    finishedItems += basket.penalties.filter(c => userSettings.hasTaskFinished(c, intraday)).length;
    inflightItems += basket.penalties.filter(c => !userSettings.hasTaskFinished(c, intraday) && !userSettings.shouldTaskRoll(c, intraday)).length;
    const pendingItems = basket.pendingItems.filter(c => c.itemType === ItemTypes.task || c.itemType === ItemTypes.penalty).length;
    const positionInfo = userSettings.getState<IPositionBasketInfo>(STATE_KEY_POSITIONS, {} as IPositionBasketInfo);
    if (!positionInfo.initialCount) positionInfo.initialCount = 0;
    if (!positionInfo.expectedCount) positionInfo.expectedCount = 0;
    const positionsMissed = positionInfo.expectedCount;
    const positionsComplete = positionInfo.initialCount - positionInfo.expectedCount;
    return finishedItems - (inflightItems * 1.5) - (pendingItems * 2) + positionsComplete - (positionsMissed * 2) - ((currentPenalties || 0) * 1.5) - ((daysMissed || 0) * 5);
}

const getInsights = (userSettings: StatefulSettings,
    daysMissed: number, currentPenalties: number, calculatedScore: number,
    rollResults: IRollResults): string => {
    if (!userSettings || !userSettings.getControl()) return '';
    const name = userSettings.getControl().username;
    const sections: string[] = [];

    if (daysMissed && daysMissed > 0) {
        sections.push(`⚠️ Attendance\n${name} has not logged on for ${daysMissed} day${daysMissed === 1 ? '' : 's'}. Their score reflects the missed activity, bank impact, and penalties accumulated during that period.`);
    }

    const { penaltyPointConversionType } = userSettings.getGeneralSettings();
    let penaltyText = '';
    if (penaltyPointConversionType === PenaltyPointConversionType.increaseCheckoutTarget) penaltyText = 'This increased their reward target and may justify further follow-up.';
    else if (penaltyPointConversionType === PenaltyPointConversionType.penalties) penaltyText = 'The accrued penalty points have already been converted into extra session penalties.';
    else penaltyText = 'Their bank has been reduced as a consequence, which will slow their progress toward rewards.';

    const basket = userSettings.getBasketItems(true);
    if (userSettings.getSessionSettings().positionSettings
        && !userSettings.getSessionSettings().positionSettings.disablePositions) {
        const positionInfo = userSettings.getPositionInfo();
        if (!positionInfo.initialCount) positionInfo.initialCount = 0;
        if (!positionInfo.expectedCount) positionInfo.expectedCount = 0;
        const positionsMissed = positionInfo.expectedCount;
        const positionsComplete = positionInfo.initialCount - positionInfo.expectedCount;
        let section = `🧍 Positions\n• Completed: ${positionsComplete}\n• Missed: ${positionsMissed}`;
        if (positionsMissed > 0) {
            section += `\n• Last missed position: ${positionInfo.name ?? 'Unknown'}\n${penaltyText}`;
        }
        sections.push(section);
    }

    const creditFinishedItems = basket.tasks.filter(c => userSettings.hasTaskFinished(c, false)).length;
    const creditInflightItems = basket.tasks.filter(c => !userSettings.hasTaskFinished(c, false) && !userSettings.shouldTaskRoll(c, false)).length;
    const rolledCreditItems = basket.tasks.filter(c => userSettings.shouldTaskRoll(c, false)).length;
    const pendingCreditItems = basket.pendingItems.filter(c => c.itemType === ItemTypes.task).length;
    if (creditFinishedItems + creditInflightItems + pendingCreditItems > 0) {
        let section = `✅ Task summary\n• Scheduled: ${basket.tasks.length + basket.pendingItems.filter(pi => pi.itemType === ItemTypes.task).length}\n• Completed: ${creditFinishedItems}`;
        if (creditInflightItems > 0) section += `\n• Started too late to finish: ${creditInflightItems}`;
        if (pendingCreditItems > 0) section += `\n• Not started: ${pendingCreditItems}`;
        if (rolledCreditItems > 0) section += `\n• Rolled into the next day: ${rolledCreditItems}`;
        if (creditInflightItems > 0 || pendingCreditItems > 0) section += `\n${penaltyText}`;
        sections.push(section);
    }

    const penaltyFinishedItems = basket.penalties.filter(c => userSettings.hasTaskFinished(c, false)).length;
    const penaltyInflightItems = basket.penalties.filter(c => !userSettings.hasTaskFinished(c, false) && !userSettings.shouldTaskRoll(c, false)).length;
    const rolledPenaltyItems = basket.penalties.filter(c => userSettings.shouldTaskRoll(c, false)).length;
    const pendingPenaltyItems = basket.pendingItems.filter(c => c.itemType === ItemTypes.penalty).length;
    if (penaltyFinishedItems + penaltyInflightItems + pendingPenaltyItems > 0) {
        let section = `⚠️ Penalty summary\n• Scheduled: ${basket.penalties.length + basket.pendingItems.filter(pi => pi.itemType === ItemTypes.penalty).length}\n• Completed: ${penaltyFinishedItems}`;
        if (penaltyInflightItems > 0) section += `\n• Started too late to finish: ${penaltyInflightItems}`;
        if (pendingPenaltyItems > 0) section += `\n• Not started: ${pendingPenaltyItems}`;
        if (rolledPenaltyItems > 0) section += `\n• Rolled into the next day: ${rolledPenaltyItems}`;
        if (penaltyInflightItems > 0 || pendingPenaltyItems > 0) section += `\n${penaltyText}`;
        sections.push(section);
    }

    if (userSettings.getSessionSettings().dailyReporting) {
        const reportComplete = userSettings.getState(STATE_KEY_REPORTCOMPLETE, false);
        sections.push(`📓 Reporting\n${reportComplete ? 'Reports were completed as required.' : 'Reports were still outstanding at the end of the day.'}`);
    }

    if (rollResults && rollResults.penalties && rollResults.penalties.length > 0) {
        sections.push(`📌 Penalty points raised\n${name} generated ${rollResults.penalties.length} penalty entries today.\n${formatMessageList(rollResults.penalties.map((pen) => `${pen.reason} — ${pen.points} point${pen.points === 1 ? '' : 's'}`))}`);
    }

    let score = calculatedScore;
    if (!score) score = getUserScore(userSettings, daysMissed, currentPenalties, false);
    let scoreText = 'Performance was below expectation and may need follow-up.';
    if (score > 10) scoreText = 'Performance was strong and showed good consistency.';
    else if (score > 0) scoreText = 'Performance was acceptable but there is room for improvement.';
    sections.push(`📈 Final assessment\n${name}'s final score was ${score}. ${scoreText}`);

    return sections.join('\n\n');
}

/* sends the owner a daily report from the user of the user settings passed in. Intraday allows the account summary to view the latest state during the day */
export function sendDailyReport(userSettings: StatefulSettings,
    daysMissed: number, rollResults: IRollResults): Promise<{ score: number, newPenaltyScore: number }> {
    return new Promise<{ score: number, newPenaltyScore: number }>((resolve) => {
        if (!userSettings.shouldSendReportForYesterday()) return resolve({ score: 0, newPenaltyScore: 0 });
        let newPenalties = 0;
        for (let i = 0; i < rollResults.penalties.length; i += 1) {
            newPenalties += rollResults.penalties[i].points;
        }

        const score = getUserScore(userSettings, daysMissed, newPenalties, false);
        sendInsightsMessage(userSettings, daysMissed, newPenalties, score, rollResults);
        resolve({ score, newPenaltyScore: newPenalties });
    });
}

const sendInsightsMessage = (userSettings: StatefulSettings, daysMissed: number, penalties: number, score: number,
    rollResults: IRollResults): void => {
    const username = userSettings.getControl().username;
    let message = `📊 Daily Activity Report\n\nMember: ${username}\nScore: ${score}\nPenalty points generated today: ${penalties}`;
    const insight = getInsights(userSettings, daysMissed, penalties, score, rollResults);
    message += `\n\n🔎 Summary\n${insight}`;
    sendMessageToOwner(MessageType.DailyScore, userSettings, message, `📊 Daily Report for ${username}`);
}

/* sends a message to a recipent from the user of the userSettings passed in */
export const sendOwnedMessage = (userSettings: StatefulSettings, toUser: IUserRecord, ownerName: string): void => {
    const message = `🔒 Session update\n\n${ownerName} now controls your session.\n\nPlease review your schedule, rules, and messages for any new instructions.`;
    sendMessage(MessageType.Owned, userSettings, message,
        '🔒 You are now owned', { username: ownerName, userid: ownerName }, [toUser]);
}

/* sends a message to a recipent from the user of the userSettings passed in */
export const sendLoanedMessage = (userSettings: StatefulSettings, toUser: IUserRecord, ownerName: string): void => {
    const message = `🔄 Session loan update\n\nYour session has been loaned to ${ownerName}.\n\nPlease keep checking your messages and daily schedule for any changes during the loan.`;
    sendMessage(MessageType.Owned, userSettings, message,
        '🔄 You are now loaned to ' + ownerName, userSettings.getControl().getOwner(), [toUser]);
}

/* sends a message to a recipent from the user of the userSettings passed in */
export const sendLoaneeMessage = (userSettings: StatefulSettings, toOwner: IUserRecord, userName: string): void => {
    const message = `🤝 Session loan granted\n\n${userName} has been placed in your care for the duration of the loan.\n\nYou can manage the session until the arrangement ends, and you may end the loan at any time using End Loan.`;
    sendMessage(MessageType.Owned, userSettings, message,
        '🤝 You have been loaned ' + userName, userSettings.getControl(), [toOwner]);
}
/* sends a message to a recipent from the user of the userSettings passed in */
export const sendSharedMessage = (userSettings: StatefulSettings, toUser: IUserRecord, ownerName: string): void => {
    const message = `👥 Session sharing update\n\nYour session has been shared with ${ownerName}.\n\nYou may now receive guidance or instructions from more than one approved person.`;
    sendMessage(MessageType.Owned, userSettings, message,
        '👥 Your session is now shared with ' + ownerName, userSettings.getControl().getOwner(), [toUser]);
}

/* sends a message to a recipent from the user of the userSettings passed in */
export const sendShareeMessage = (userSettings: StatefulSettings, toOwner: IUserRecord, userName: string): void => {
    const message = `👥 Shared access granted\n\n${userName} has been shared with you.\n\nYou can participate in the session until the share ends, and you may stop it at any time using End Share.`;
    sendMessage(MessageType.Owned, userSettings, message,
        '👥 You have been shared ' + userName, userSettings.getControl(), [toOwner]);
}

export const sendHouseRules = (userSettings: StatefulSettings,
    profile: IOwnerProfileSession | undefined,
    profileSettings: ISessionSettings, owner?: ControlRecord): void => {
    let message = '📜 House Rules\n\nYour session runs daily, and you are expected to follow the guidance, schedule, and rules provided to you.';
    if (!profileSettings.permissionSettings.disablePermissions) {
        message = `${message}\n\n📝 Daily permissions\nChoices around everyday activities can be controlled through the permissions system. Check them regularly and follow what has been approved.`;
        message = `${message}\n\nCurrent standard permissions:\n`;
        profileSettings.permissionSettings.permissions.filter(p => p
            && isAvailableItem(p)
            && p.eventType === EventType.standard).forEach(p => message += `• ${p.name}\n`);
    }
    if (!profileSettings.eventsSettings.disableEvents) message = `${message}\n\n⏰ Timed events\nSome parts of your day will appear at set times. Keep an eye on your schedule and complete them when they appear to avoid unnecessary penalties.`;
    if (profileSettings.dailyReporting) message = `${message}\n\n📓 Daily reports\nYou are expected to send your reports when required. Use them to explain how the day went, what was difficult, and anything important that happened.`;
    if (!profileSettings.creditSettings.dailyMandatoryCredits) message = `${message}\n\n💰 Daily tasks and rewards\nYou will be given tasks that help build your score and bank balance. The more consistently you complete, the more progress you make.`;
    message = `${message}\n\n🎒 Items and preparation\nMake sure your owned items are recorded accurately. Tasks may be selected based on what you say you have available, so keep this up to date.`;
    if (!profileSettings.creditSettings.scheduleDailyTasks) message = `${message}\n\n📅 Ongoing check-ins\nTasks may be scheduled across the day, so check back regularly in case something new has been assigned.`;
    if (!profileSettings.creditSettings.credits.filter(c =>
        isAvailableItem(c)
        && !c.preSelect)) message = `${message}\n\n🔁 Repeating tasks\nSome tasks may be selected regularly. If an item is marked as permanent, it stays active until it is formally cleared.`;
    if (!profileSettings.positionSettings.disablePositions) message = `${message}\n\n🧍 Positions\nPosition practice may be included in your day. Completing them properly helps your score and shows consistency.`;
    message = `${message}\n\n📈 Daily review\nA summary is sent each day covering what you completed, what you missed, and where you may need to improve. Keep an eye on your score and aim to finish the day well.`;
    if (profileSettings.penaltyPointConversionType === PenaltyPointConversionType.penalties) message = `${message}\n\n⚠️ Penalties\nIf mistakes build up, extra tasks or punishments may be added on top of your usual routine.`;
    if (profileSettings.penaltyPointConversionType === PenaltyPointConversionType.increaseCheckoutTarget) message = `${message}\n\n⚠️ Penalty impact\nMistakes can increase the amount you need to earn before certain rewards become available.`;
    if (profileSettings.penaltyPointConversionType === PenaltyPointConversionType.decreaseBankBalance) message = `${message}\n\n⚠️ Bank deductions\nMistakes can reduce your bank balance, which means slower progress toward rewards and permissions.`;
    // Session Lock criteria
    if (profile && profile.lockOnSelection) {
        message = `${message}\n\n🔐 Session lock\nYour settings are locked for now and will only become editable once the required conditions are met.`;
        if (profile.lockType === LockTypes.duration) message = `${message}\n\nThe lock is time-based, so it will remain in place until the required number of days or weeks has been served.`;
        if (profile.lockType === LockTypes.target) message = `${message}\n\nThe lock is target-based, so you will need to earn the required amount through tasks and steady progress before it is lifted.`;
    }
    if (owner) sendMessageToMember(MessageType.Rules, userSettings, message,
        '📜 House Rules', [userSettings.getControl()], owner);
    else sendMessageToMember(MessageType.Rules, userSettings, message,
        '📜 House Rules', [userSettings.getControl()], { userid: userSettings.getControl().userid, username: profile?.name as string });
}
export const sendPilloryCompleteMessageToOwner = (
    userSettings: Settings,
    item: PilloryTopic) => {
    if (!userSettings.getControl().isOwned() || !item.itemAdditionOptions) return;
    return sendMessageToOwner(MessageType.PilloryResult, userSettings,
        `🗳️ Pillory result ready\n\n${userSettings.getControl().username} has completed their pillory period, and the outcome has now been applied to their schedule.\n\nResult applied: ${convertToCountTypeString(item.itemAdditionOptions.expectedCount as number, item.countType)}`,
        `🗳️ ${item.name} pillory completed for ${userSettings.getControl().username}`);
}

export const sendPilloryAddedMessageToOwner = (
    userSettings: Settings,
    item: PilloryTopic) => {
    if (!userSettings.getControl().isOwned()) return;
    return sendMessageToOwner(MessageType.AddedToPillory, userSettings,
        `🗳️ ${userSettings.getControl().username} has been placed in pillory.\n\nCommunity voting is now open. You can track progress on the Pillory page, and a follow-up message will be sent when the result is final.`,
        `🗳️ ${item.name} added to pillory for ${userSettings.getControl().username}`);
}

export const sendPenaltyAddedMessageToOwner = (
    userSettings: Settings,
    item: IPenaltyItem,
    reason: string) => {
    if (!userSettings.getControl().isOwned()) return;
    return sendMessageToOwner(MessageType.AddedToPillory, userSettings,
        `⚠️ Penalty added to session\n\nMember: ${userSettings.getControl().username}\nPenalty: ${item?.name}\nReason: ${reason}`,
        `⚠️ ${item.name} penalty added for ${userSettings.getControl().username}`);
}

export const sendRouletteAddedMessageToOwner = (
    userSettings: Settings,
    item: ISettingsItem) => {
    if (!userSettings.getControl().isOwned()) return;
    return sendMessageToOwner(MessageType.AddedByRoulette, userSettings,
        `🎡 Roulette outcome\n\n${userSettings.getControl().username} has had ${item.name} added to today's schedule by the wheel.\n\nYou can review progress from the Today tab of their session.`,
        `🎡 ${item.name} added by Wheel Of Fortune for ${userSettings.getControl().username}`);
}

export const sendRoulettePilloryAddedMessageToOwner = (
    userSettings: Settings,
    item: ISettingsItem) => {
    if (!userSettings.getControl().isOwned()) return;
    return sendMessageToOwner(MessageType.AddedByRoulette, userSettings,
        `🎡 Roulette outcome\n\n${userSettings.getControl().username} has had ${item.name} sent to pillory by chance.\n\nThe result will depend on the community vote, and you can monitor it on the Pillories screen.`,
        `🎡 ${item.name} pillory added by Wheel Of Fortune for ${userSettings.getControl().username}`);
}

export const sendRouletteVoteAddedMessageToOwner = (
    userSettings: Settings,
    item: ISettingsItem,
    vote: ITaskVoteConfig) => {
    if (!userSettings.getControl().isOwned()) return;
    return sendMessageToOwner(MessageType.AddedByRoulette, userSettings,
        `🎡 Roulette outcome\n\n${userSettings.getControl().username} has had the vote on ${item.name} ${vote.isUp ? 'increased' : 'decreased'} by ${convertToCountTypeString(vote.voteIncrement, vote.countType)}.\n\nYou can review the latest position on the Votes screen.`,
        `🎡 ${item.name} vote changed by Wheel Of Fortune for ${userSettings.getControl().username}`);
}


export const sendItemRequestToOwner = (
    userSettings: Settings,
    item: ISettingsItem) => {
    if (!userSettings.getControl().isOwned()) return;
    return sendMessageToOwner(MessageType.RequestedTaskByMember, userSettings,
        `🙋 Task request\n\n${userSettings.getControl().username} would like ${item.name} enabled in their session.\n\nYou can enable it directly in their settings or add it to your owner profile if you want it available more broadly.`,
        `🙋 ${item.name} requested by ${userSettings.getControl().username}`,
        { userid: userSettings.getControl().userid, item });
}

export const sendItemRemovalRequestToOwner = (
    userSettings: Settings,
    item: ISettingsItem) => {
    if (!userSettings.getControl().isOwned()) return;
    return sendMessageToOwner(MessageType.RequestedRemoveTaskByMember, userSettings,
        `🗓️ Today's removal request\n\n${userSettings.getControl().username} is asking for ${item.name} to be removed from today's schedule.`,
        `🗓️ ${item.name} removal for today requested by ${userSettings.getControl().username}`,
        { userid: userSettings.getControl().userid, item });
}
export const sendItemDeleteRequestToOwner = (
    userSettings: Settings,
    item: ISettingsItem) => {
    if (!userSettings.getControl().isOwned()) return;
    return sendMessageToOwner(MessageType.RequestedDeleteTaskByMember, userSettings,
        `🧹 Session item removal request\n\n${userSettings.getControl().username} would like ${item.name} hidden or disabled from their session.\n\nYou can review and apply this in their settings or owner profile.`,
        `🧹 ${item.name} delete task requested by ${userSettings.getControl().username}`,
        { userid: userSettings.getControl().userid, item });
}
export const sendItemRequestAvailabilityReviewToOwner = (
    userSettings: Settings,
    item: ISettingsItem) => {
    if (!userSettings.getControl().isOwned()) return;
    return sendMessageToOwner(MessageType.ReviewAvailability, userSettings,
        `📅 Availability review requested\n\n${userSettings.getControl().username} is asking for ${item.name} to be limited to free days only.\n\nCurrent availability: ${AvailabilityDayType[item.availabilityDayType]}\nReason: they currently have a busy day and cannot complete it.\n\nApprove below if you want to update it for their session.`,
        `📅 ${userSettings.getControl().username} availability request for ${item.name}`,
        { userid: userSettings.getControl().userid, item });
}

export const sendItemRequestMoreInfoToOwner = (
    userSettings: Settings,
    item: { name: string },
    info: string) => {
    if (!userSettings.getControl().isOwned()) return;
    return sendMessageToOwner(MessageType.RequestedTaskInfoByMember, userSettings,
        `❓ Clarification requested\n\n${userSettings.getControl().username} is asking for more information about ${item.name}.\n\nTheir note:\n${info}`,
        `❓ ${item.name} task information requested by ${userSettings.getControl().username}`,
        { userid: userSettings.getControl().userid, item });
}

export const sendItemRequestPleadVoteToOwner = (
    userSettings: Settings,
    item: ISettingsItem,
    info: string) => {
    if (!userSettings.getControl().isOwned()) return;
    return sendMessageToOwner(MessageType.RequestedTaskVoteUpdateByMember, userSettings,
        `🙏 Vote reduction request\n\n${userSettings.getControl().username} is asking for mercy on ${item.name} and would like the vote reduced.\n\nTheir note:\n${info}`,
        `🙏 ${item.name} task vote reduction requested by ${userSettings.getControl().username}`,
        { userid: userSettings.getControl().userid, item });
}

export const sendItemRequestSettingsChangeToOwner = (
    userSettings: Settings,
    item: ISettingsItem,
    info: string) => {
    if (!userSettings.getControl().isOwned()) return;
    return sendMessageToOwner(MessageType.RequestedTaskUpdateByMember, userSettings,
        `🛠️ Settings update request\n\n${userSettings.getControl().username} would like ${item.name} to be ${item.isNew ? "added" : "updated"} in their settings.\n\nTheir note:\n${info}`,
        `🛠️ ${item.name} ${item.isNew ? "new task" : "task update"} requested by ${userSettings.getControl().username}`,
        { userid: userSettings.getControl().userid, item });
}

export const sendPreviewMessageToMember = (userSettings: StatefulSettings,
    toUser: IUserRecord,
    items: IBasket,
    suppressNotification?: boolean, subjectOverride?: string, initiatorUser?: IUserRecord): void => {
    let itemsToBring: (ITaskTimerInfo | IPenaltyTimerInfo)[]
        = items.pendingItems.filter(p => {
            const task = (p as unknown as ITaskTimerInfo | IPenaltyTimerInfo);
            return task && (task.isItem || task.selectedItems);
        }) as unknown as (ITaskTimerInfo | IPenaltyTimerInfo)[];
    if (items.childTasks) {
        Object.values(items.childTasks).forEach(p => {
            itemsToBring = itemsToBring.concat(p.filter(innerTask => innerTask && (innerTask.isItem || innerTask.selectedItems)));
        });
    }
    const distinctItems: string[] = [];
    itemsToBring.forEach(i => {
        let key: string;
        if (i.isItem) {
            key = `${i.selectedCondition ? `${i.selectedCondition} ` : ''}${userSettings.getCurrentItem(i)?.name}`;
            if (distinctItems.indexOf(key) === -1) distinctItems.push(key);
        } else {
            const selectedItems = i.selectedItems;
            if (selectedItems) Object.keys(selectedItems).forEach(k => {
                key = selectedItems[k];
                if (distinctItems.indexOf(key) === -1) distinctItems.push(key);
            });
        }
    });

    const uniqueRequiredItems = Array.from(new Set(distinctItems));
    let message = `📦 ${subjectOverride ?? 'Tomorrow preview'}`;
    if (uniqueRequiredItems.length > 0) {
        message += `\n\nPlease have the following ready:\n\n${formatMessageList(uniqueRequiredItems)}`;
    } else {
        message += `\n\nNo specific items are listed yet, but please still check your session tomorrow in case anything changes.`;
    }

    if (items.chanceTasks && items.chanceTasks.length > 0) {
        const possibleExtras: (ITaskTimerInfo | IPenaltyTimerInfo)[]
            = items.chanceTasks.filter(p => {
                const task = (p as unknown as ITaskTimerInfo | IPenaltyTimerInfo);
                return task.isItem || task.selectedItems;
            }) as unknown as (ITaskTimerInfo | IPenaltyTimerInfo)[];

        const distinctExtraItems: string[] = [];
        possibleExtras.forEach(i => {
            let key: string;
            if (i.isItem) {
                key = `${i.selectedCondition ? `${i.selectedCondition} ` : ''}${userSettings.getCurrentItem(i)?.name}`;
                if (distinctItems.indexOf(key) === -1 && distinctExtraItems.indexOf(key) === -1) distinctExtraItems.push(key);
            } else {
                const selectedItems = i.selectedItems;
                if (selectedItems) Object.keys(selectedItems).forEach(k => {
                    key = selectedItems[k];
                    if (distinctItems.indexOf(key) === -1 && distinctExtraItems.indexOf(key) === -1) distinctExtraItems.push(key);
                });
            }
        });
        if (distinctExtraItems.length > 0) {
            message += `\n\n🎲 Possible extra items\nSome chance-based tasks may also require:\n\n${formatMessageList(Array.from(new Set(distinctExtraItems)))}`;
        }
    }
    if (items.possiblePenalties && items.possiblePenalties.length > 0) {
        const possibleExtras: (ITaskTimerInfo | IPenaltyTimerInfo)[]
            = items.possiblePenalties.filter(p => {
                const task = (p as unknown as ITaskTimerInfo | IPenaltyTimerInfo);
                return task.isItem || task.selectedItems;
            }) as unknown as (ITaskTimerInfo | IPenaltyTimerInfo)[];

        const distinctExtraItems: string[] = [];
        possibleExtras.forEach(i => {
            let key: string;
            if (i.isItem) {
                key = `${i.selectedCondition ? `${i.selectedCondition} ` : ''}${userSettings.getCurrentItem(i)?.name}`;
                if (distinctItems.indexOf(key) === -1 && distinctExtraItems.indexOf(key) === -1) distinctExtraItems.push(key);
            } else {
                const selectedItems = i.selectedItems;
                if (selectedItems) Object.keys(selectedItems).forEach(k => {
                    key = selectedItems[k];
                    if (distinctItems.indexOf(key) === -1 && distinctExtraItems.indexOf(key) === -1) distinctExtraItems.push(key);
                });
            }
        });
        if (distinctExtraItems.length > 0) {
            message += `\n\n⚠️ Possible penalty items\nIf extra penalties are triggered, you may also need:\n\n${formatMessageList(Array.from(new Set(distinctExtraItems)))}`;
        }
    }

    const lastSentMessage = userSettings.getState("lastSentMessage", "");
    if (lastSentMessage === message) return; // don't keep sending the same message
    userSettings.updateState("lastSentMessage", message);

    if (!suppressNotification) {
        userSettings.showMessage({
            message, notificationType: NotificationType.PreviewTomorrow
        });
    }
    sendMessage(
        MessageType.PreviewDay,
        userSettings,
        message,
        subjectOverride ?? 'Items Required Tomorrow',
        initiatorUser ?? userSettings.getControl().getOwner(),
        [toUser]);

    sendMessageToOwner(MessageType.PreviewDay,
        userSettings, `📦 Preview generated for tomorrow\n\n${toUser.username} has requested a preview so they can prepare in advance.\n\nIf you add more tasks for tomorrow, the preview will be sent again.\n\nMessage sent to them:\n\n${message}`,
        `📦 Items Required by ${toUser.username} Tomorrow`)
}

export const sendRequiredEventsMessageToMember = (userSettings: Settings,
    toUser: IUserRecord,
    items: IBasket,
    suppressNotification?: boolean): void => {

    const itemsToBring: BaseBasketItem[]
        = items.pendingItems.filter(p => {
            if (p instanceof ScheduleBasketItem
                && p.startTime
                && p.itemType === ItemTypes.event) {
                const settingItem = userSettings.getEvent(p.key) ?? p as unknown as IEventItem; // this means we try our best with the actual event in the case where it is a custom schedule and won't be found in settings
                return settingItem && ((settingItem.missEventPenalties && settingItem.missEventPenalties.length > 0) || (settingItem.missEventPillories && settingItem.missEventPillories.length > 0));
            }
            return false;
        });

    if (itemsToBring.length === 0) return;
    const getEventTitle = (item: BaseBasketItem): string => {
        const settingItem = userSettings.getEvent(item.key) ?? item as unknown as IEventItem;
        const explicitTitle = ((settingItem as any)?.title ?? '').trim();
        if (explicitTitle) return explicitTitle;
        const rawTitle = (settingItem?.name ?? item.name ?? '').trim();
        const firstLine = rawTitle.split('\n')[0].trim();
        const rawDescription = (settingItem?.description ?? item.description ?? '').trim();
        if (rawDescription && firstLine.endsWith(rawDescription)) {
            return firstLine.slice(0, firstLine.length - rawDescription.length).replace(/[\s\-–—:]+$/, '');
        }
        return firstLine;
    };
    const eventMessages = [...itemsToBring]
        .sort((a, b) => {
            const parseEventTime = (item: BaseBasketItem): number => {
                const [hours, minutes] = formatValidDate((item as ScheduleBasketItem).startTime as Date, 'HH:mm')
                    .split(':')
                    .map(Number);
                return (hours * 60) + minutes;
            };
            const timeA = parseEventTime(a);
            const timeB = parseEventTime(b);
            return timeB - timeA;
        })
        .map((item) => `${getEventTitle(item)} at ${formatValidDate((item as ScheduleBasketItem).startTime as Date, 'HH:mm')}`);
    const message = `⏰ Events you must not miss today\n\nPlease be online for the following times:\n\n${formatMessageList(eventMessages)}`;
    if (!suppressNotification) {
        userSettings.showMessage({
            message, notificationType: NotificationType.RequiredEvents
        });
    }
    sendMessage(
        MessageType.EventsNotToMiss,
        userSettings,
        message,
        '⏰ Events you must not miss today',
        userSettings.getControl().getOwner(),
        [toUser]);

    sendMessageToOwner(MessageType.EventsNotToMiss,
        userSettings, `⏰ Attendance reminder sent\n\n${toUser.username} has events today that may incur penalties if missed.\n\nMessage sent to them:\n\n${message}`,
        `⏰ Events ${toUser.username} must not miss today`)
}

export const sendOfferMessageToMember = (
    userSettings: Settings, toUser: IUserRecord, isAlreadyOwned?: boolean): void => {
    userSettings.showMessage({ message: '📨 Member message sent', notificationType: NotificationType.MessageSent });
    const ownerName = userSettings.getControl().username;
    const message = isAlreadyOwned
        ? `🤝 Supervisor request\n\n${ownerName} would like to join your session as an additional supervisor.\n\nYour current owner would remain in place. If you want to accept, choose Accept Offer at the bottom of this message.\n\nIf you delete this message, the offer expires and they will need to send it again.`
        : `💌 Session offer\n\n${ownerName} would like to take ownership of your session.\n\nIf you want to accept, choose Accept Offer at the bottom of this message.\n\nIf you delete this message, the offer expires and they will need to send it again.`;
    sendMessage(
        MessageType.OwnershipRequest,
        userSettings,
        message,
        isAlreadyOwned ? '🤝 Supervisor Request' : '💌 Session Offer',
        userSettings.getControl(),
        [toUser]);
}

export const sendBankIncreaseMessageToMember = (
    perk: IReward, reason: string | undefined, fromUser: Settings, toUser: IUserRecord): void => {
    sendMessage(
        MessageType.MemberMessage,
        fromUser,
        `💰 Reward received\n\n${fromUser.getControl().username} has transferred £${perk.cost} into your bank${reason ? ' for ' + reason : ''}.\n\nGreat work — the amount is now available in your balance.`,
        `💰 Bank Reward from ${fromUser.getControl().username}`,
        fromUser.getControl(),
        [toUser]);
}

export const sendPerkMessageToMember = (
    perk: IReward, reason: string | undefined, fromUser: Settings, toUser: IUserRecord): void => {
    sendMessage(
        MessageType.PerkReward,
        fromUser,
        `🎁 Reward available\n\n${fromUser.getControl().username} has offered you the reward ${perk.name}${reason ? ' for ' + reason : ''}.\n\nKeep this message until you are ready to use it, then press Redeem to notify your owner.`,
        `🎁 ${perk.name} Reward from ${fromUser.getControl().username}`,
        fromUser.getControl(),
        [toUser]);
}

/**
 * sends message to owner of the userSettings from the member
 * will set up a read receipt for the userSettings member to view
 * when the sender isn't the system itself will copy the message for the system to have a copy
 */
export const sendMessageToOwner = (messageType: MessageType,
    userSettings: Settings,
    message: string, subject: string,
    state?: any): void => {
    userSettings.showMessage({ message: '📨 Owner message sent', notificationType: NotificationType.MessageSent });
    const owner = userSettings.getControl().getOwner();
    let mutedMessageTypes: MessageType[] = [];
    if (owner) {
        const ownerRecord = userSettings.getAllControlRecords().find(u => owner.userid === u.userid);
        if (ownerRecord) {
            mutedMessageTypes = ownerRecord.mutedMessageTypes ?? [];
        }
    }
    sendMessage(
        messageType,
        userSettings,
        message,
        subject,
        userSettings.getControl(),
        [owner],
        undefined,
        mutedMessageTypes,
        state);
}

/**
 * sends message to owner of the userSettings from the member
 * will set up a read receipt for the userSettings member to view
 * when the sender isn't the system itself will copy the message for the system to have a copy
 */
export const sendMessageToSelf = (messageType: MessageType, userSettings: Settings, message: string, subject: string): void => {
    userSettings.showMessage({ message: '📨 Message sent', notificationType: NotificationType.MessageSent });
    sendMessage(
        messageType,
        userSettings,
        message,
        subject,
        userSettings.getControl().getOwner() as ControlRecord,
        [userSettings.getControl()]);
}

/**
 * sends message to owner of the userSettings from the member
 * will set up a read receipt for the userSettings member to view
 * when the sender isn't the system itself will copy the message for the system to have a copy
 */
export const sendMessageToMember = (
    messageType: MessageType,
    userSettings: Settings,
    message: string,
    subject: string,
    toUsers: IUserRecord[],
    fromUser?: IUserRecord,
    ignoreSelfOwned?: boolean,
    mutedMessageTypes?: MessageType[]): void => {
    const globallyMuted = mutedMessageTypes ?? [];
    if (globallyMuted.indexOf(messageType) > -1) {
        void userSettings.auditUser(new AuditMessage(
            AuditActionTypes.Settings,
            'Message Muted',
            'Message has be muted for the user',
            TraceLevel.info,
        ));
        return;
    }

    const controls = userSettings.getAllControlRecords?.() ?? [];
    const filteredRecipients = toUsers.filter((user) => {
        const controlRecord = controls.find((u) => u.userid === user.userid);
        const recipientMuted = controlRecord?.mutedMessageTypes ?? [];
        return recipientMuted.indexOf(messageType) === -1;
    });

    if (filteredRecipients.length === 0) {
        void userSettings.auditUser(new AuditMessage(
            AuditActionTypes.Settings,
            'Message Muted',
            'Message muted for all recipients',
            TraceLevel.info,
        ));
        return;
    }

    sendMessage(
        messageType,
        userSettings,
        message,
        subject,
        fromUser ? fromUser : userSettings.getControl(),
        filteredRecipients,
        ignoreSelfOwned,
        undefined);
}

export const sendHolidayModeMessageToMember = (holidayMode: boolean, toUserSettings: Settings): void => {
    sendMessageToMember(
        MessageType.HolidayModeChanged,
        toUserSettings,
        `🏖️ Holiday mode is now ${holidayMode ? 'enabled. Your schedule is paused until the settings are changed again.' : 'disabled. Your normal schedule will resume and your task list will refresh.'}`,
        '🏖️ Holiday mode updated',
        [toUserSettings.getControl()],
        toUserSettings.getControl().getOwner(),
        true
    );
}

export const sendMessageToSystem = (messageType: MessageType, userSettings: Settings, message: string, subject: string): void => {
    sendMessage(
        messageType,
        userSettings,
        message,
        subject,
        userSettings.getControl(),
        [userSettings.getSystemUser() as IControl]);
}

/**
 * sends a request of proof message with a unique code to the member and copies in the owner so they have reference to cross check
 */
export const sendRequestProof = (item: TaskBasketItem | PenaltyBasketItem,
    toUserSettings: StatefulSettings,
    requester?: IUserRecord,
    ignoreSend?: boolean): string => {
    const itemDescription = typeof (item as any).getBasketText === 'function'
        ? item.getBasketText(toUserSettings.checkSettingItemEventTriggerValid)
        : (item.description || item.name);
    const verifyID = window.btoa(new Date().getMilliseconds().toString());
    void toUserSettings.auditUser(new AuditMessage(
        AuditActionTypes.Request,
        item.name,
        'Proof Requested - ' + verifyID,
        TraceLevel.info,
    ));

    const owner = requester ?? toUserSettings.getControl().getOwner();
    const sender = toUserSettings.getControl().isOwnedBySelf() ? 'the System' : owner.username;
    const validationMethod = toUserSettings.getControl().isOwnedBySelf() ? 'post it to your personal blog, a social link, or Discord as evidence' : `share it with ${owner.username} using a personal blog, social link, or Discord chat`;
    const requestText = `📸 Proof requested\n\nRequested by: ${sender}\nTask: ${itemDescription}\nReference code: ${verifyID}\n\nPlease provide photographic evidence as soon as possible and ${validationMethod}. A matching copy of this reference has also been sent for validation.`;
    const requestTitle = `📸 Proof of ${item.name} Request - ${verifyID}`;
    if (!ignoreSend) {
        sendMessageToMember(
            MessageType.ProofRequest,
            toUserSettings,
            requestText,
            requestTitle,
            [toUserSettings.getControl()],
            owner,
        );
    }
    return `📸 ${toUserSettings.getControl().username} has been asked to provide proof of ${itemDescription}. Reference code: ${verifyID}.`;
}

export const sendUrlRequestToPurchase = (item: IShoppingRequest,
    userSettings: StatefulSettings) => {
    if (!userSettings.getControl().isOwned()) return;
    const itemDescription = `${item.item.name}. Extra information: ${item.description}\n\n${item.url ? `Link: ${item.url}` : ''}`;
    const requestText = `🛍️ Purchase request\n\n${userSettings.getControl().username} has requested approval to buy ${item.item.name} for their session.\n\n${itemDescription}`;
    const requestTitle = `🛍️ Purchase of ${item.item.name} Requested by ${userSettings.getControl().username}`;
    return sendMessageToOwner(MessageType.ShoppingRequest, userSettings,
        requestText, requestTitle, { userid: userSettings.getControl().userid, item });
}

export const sendUrlRequestPurchase = (item: IShoppingRequest,
    toUserSettings: StatefulSettings,
    requester?: IUserRecord,
    ignoreSend?: boolean): string => {
    const itemDescription = `${item.item.name}. ${item.description}\n\n${item.url}\n\n`;
    const verifyID = window.btoa(new Date().getMilliseconds().toString());
    void toUserSettings.auditUser(new AuditMessage(
        AuditActionTypes.Request,
        item.item.name,
        'Proof Requested - ' + verifyID,
        TraceLevel.info,
    ));

    const owner = requester ?? toUserSettings.getControl().getOwner();
    const sender = toUserSettings.getControl().isOwnedBySelf() ? 'the System' : owner.username;
    const requestText = `🛍️ Purchase requested\n\nRequested by: ${sender}\nItem: ${item.item.name}\nReference code: ${verifyID}\n\n${itemDescription}Please provide photographic proof once it has been purchased. A matching copy of this reference has also been sent for validation.`;
    const requestTitle = `🛍️ Proof of purchase for ${item.item.name} - ${verifyID}`;

    if (!ignoreSend) {
        sendMessageToMember(
            MessageType.PurchaseRequest,
            toUserSettings,
            requestText,
            requestTitle,
            [toUserSettings.getControl()],
            owner,
        );
    }
    return `🛍️ ${toUserSettings.getControl().username} has been asked to provide proof of purchase for ${item.item.name}. Reference code: ${verifyID}.`;
}

export const sendPostponeItemRequestMessageToOwner = (item: ISettingsItem, userSettings: Settings): void => {
    if (!userSettings.getControl().isOwned()) return;
    return sendMessageToOwner(MessageType.ItemPostponeRequest, userSettings,
        `⏭️ Postpone request\n\n${userSettings.getControl().username} would like ${item.name} moved to tomorrow.\n\nYou can approve this using the action at the bottom of the message.`,
        `⏭️ ${item.name} requested to be postponed by ${userSettings.getControl().username}`,
        { userid: userSettings.getControl().userid, key: item.key });
}

export const sendRequestPurchase = (item: ITaskItem, toUserSettings: Settings): void => {
    void toUserSettings.auditUser(new AuditMessage(
        AuditActionTypes.Request,
        item.name,
        'Purchase Requested - ' + item.name,
        TraceLevel.info));
    const owner = toUserSettings.getControl().getOwner();
    if (toUserSettings instanceof StatefulSettings) {
        toUserSettings.addShoppingRequest({ item, description: '', url: '', key: v1(), requestedBy: owner }, true);
    }
    const sender = toUserSettings.getControl().isOwnedBySelf() ? 'the System' : owner.username;
    const verifyID = window.btoa(new Date().getMilliseconds().toString());
    const requestText = `🛍️ Purchase requested\n\nRequested by: ${sender}\nItem: ${item.name}\nReference code: ${verifyID}\n\nPlease purchase this item to continue your session and provide photographic proof when it has been obtained.`;
    const requestTitle = `🛍️ Proof of purchase for ${item.name} - ${verifyID}`;

    sendMessageToMember(
        MessageType.PurchaseRequest,
        toUserSettings,
        requestText,
        requestTitle,
        [toUserSettings.getControl()],
        owner,
    );
}

const validateProfanities = (message: string, config?: ISystemConfig): string | undefined => {
    let valid = true;
    if (!config) return;
    for (let i = 0; i < config.profanities.length; i++) {
        const word = config.profanities[i]
        const matchIdx = message.indexOf(word)
        if (matchIdx > -1) {
            const startsWithMatch = matchIdx === 0 || !message[matchIdx - 1].match(/[A-Za-z.,!?]/i)
            const endsWithMatch = matchIdx + word.length === message.length || !message[matchIdx + word.length].match(/[A-Za-z.,!?]/i)
            if (startsWithMatch && endsWithMatch) valid = false
        }
    }
    if (!valid) {
        return '⚠️ Profanity detected. Please rewrite your message without swearing.';
    }
    return undefined;
}

const validateSyntax = (message: string, config?: ISystemConfig): string | undefined => {
    let valid = true
    if (!config) return;
    const matches = [];
    for (let i = 0; i < config.prohibitedSyntax.length; i++) {
        const word = config.prohibitedSyntax[i]
        const matchIdx = message.indexOf(word)
        if (matchIdx > -1) {
            const startsWithMatch = matchIdx === 0 || !message[matchIdx - 1].match(/[A-Za-z.,!?@]/i)
            const endsWithMatch = matchIdx + word.length === message.length || !message[matchIdx + word.length].match(/[A-Za-z.,!?@]/i)
            if (startsWithMatch && endsWithMatch) {
                valid = false;
                matches.push(word);
            }
        }
    }
    if (!valid) {
        return `⚠️ Style check failed for "${matches.join(', ')}". Please refer to yourself in lowercase and others in uppercase.`;
    }
    return undefined;
}

const troublesomeChars: Record<string, string[]> = {
    '\'': ['`', '"', '’']
};

export const validateLines = (expectedCount: number, match: string, message: string, callback: (isOK: boolean, count: number, mistakes: number) => void): void => {
    Object.keys(troublesomeChars).forEach(k => troublesomeChars[k].forEach(c => message = message.replaceAll(c, k)));
    Object.keys(troublesomeChars).forEach(k => troublesomeChars[k].forEach(c => match = match.replaceAll(c, k)));
    const lines = message.split('\n').filter(line => line);
    const goodLines = lines.filter(line => line === match).length;
    let badLines = lines.filter(line => line !== match).length;
    const lastLineInProgress = match.startsWith(lines[lines.length - 1]) && match !== lines[lines.length - 1];
    if (lastLineInProgress) badLines -= 1;
    callback(expectedCount <= goodLines, goodLines, badLines > -1 ? badLines : 0);
}

export const validateGrammar = (message: string, config: ISystemConfig | undefined, callback: (isOK: boolean, message: string) => void): void => {
    const errors: string[] = [];
    const error1 = validateProfanities(message, config);
    if (error1) errors.push(error1);
    const error2 = validateSyntax(message, config);
    if (error2) errors.push(error2);
    const hasErrors = errors.length > 0;
    if (hasErrors) callback(false, errors.join('\n'));
    else callback(true, '✅ Grammar looks good');
}

export const sendLinesToOwner = (
    lines: string,
    line: string,
    expectedCount: number,
    mistakes: number,
    taskKey: string,
    settings: Settings,
    allowRedoLines: boolean,
    stats: ITypingStats): void => {
    // Keystrokes: ${stats.keystrokes}
    sendMessageToOwner(MessageType.ProofLines,
        settings,
        `✍️ Lines completed\n\n${settings.getControl().username} has completed their lines attempt.\n\n• Lines entered: ${lines.split('\n').filter(line => line).length}\n• Mistakes: ${mistakes}\n• Delete attempts: ${stats.deletekeys}\n• Words: ${stats.words}\n• Words per minute: ${stats.wordsperminute}\n• Total time spent: ${convertToMinutes(stats.timespent)}\n\nSubmitted text:\n${lines}`,
        '✍️ Lines completed', { mistakes, taskKey, expectedCount, line, allowRedoLines });
}

const sendMessage = (
    messageType: MessageType,
    settings: Settings,
    message: string,
    subject: string,
    fromUser: IUserRecord,
    toUsers: IUserRecord[],
    ignoreSelfOwned?: boolean,
    mutedMessageTypes?: MessageType[],
    state?: any): void => {

    if (muteAllMessages) {
        return;
    }

    if (mutedMessageTypes && mutedMessageTypes.indexOf(messageType) > -1) {
        void settings.auditUser(new AuditMessage(
            AuditActionTypes.Settings,
            'Message Muted',
            'Message has be muted for the user',
            TraceLevel.info
        ));
        return;
    }

    const systemUser = settings.getSystemUser();
    const auditMessage = systemUser !== undefined
        && (systemUser.mutedMessageTypes === undefined
            || systemUser.mutedMessageTypes === null
            || systemUser.mutedMessageTypes.indexOf(messageType) === -1);

    void settings.sendMessage(message, subject,
        fromUser.userid, fromUser.username,
        auditMessage,
        configCache.sentReceiptMessageType(messageType),
        messageType, toUsers, ignoreSelfOwned, state);
}

export const sendExceptionMessage = (userSettings: Settings, error: any, location?: string) => {
    sendMessageToSystem(MessageType.MemberMessage, userSettings,
        `⚠️ An unhandled error occurred.\n\nMessage: ${error.message}\n\nStack:\n${error.stack}\n\nLocation: ${location}\n\nVersion: ${import.meta.env.VITE_VERSION}`, '⚠️ Unhandled Error Report');
}

export const sendSetAvailableDaysMessage = (userSettings: StatefulSettings) => {
    void configCache.fetchConfig().then((config) => sendMessage(MessageType.Owned, userSettings, config.system && config.system.memberAvailableDays || '',
        '🗓️ Todo: Set Available Days', userSettings.getSystemUser() as IUserRecord, [userSettings.getControl()]));
}

export const sendSetItemsMessage = (userSettings: StatefulSettings) => {
    void configCache.fetchConfig().then((config) => sendMessage(MessageType.Owned, userSettings, config.system && config.system.memberOwnedItems || '',
        '🎒 Todo: Set Owned Items', userSettings.getSystemUser() as IUserRecord, [userSettings.getControl()]));
}

export const sendEditUserProfileMessage = (userSettings: StatefulSettings) => {
    void configCache.fetchConfig().then((config) => sendMessage(MessageType.Owned, userSettings, config.system && config.system.memberEditProfile || '',
        '👤 Todo: Update User Profile', userSettings.getSystemUser() as IUserRecord, [userSettings.getControl()]));
}

export const sendEditUserSettingsMessage = (userSettings: StatefulSettings) => {
    void configCache.fetchConfig().then((config) => sendMessage(MessageType.Owned, userSettings, config.system && config.system.memberEditSettings || '',
        '⚙️ Todo: Explore Session Settings', userSettings.getSystemUser() as IUserRecord, [userSettings.getControl()]));
}

export const sendWelcomeOwnerMessage = (userSettings: StatefulSettings) => {
    void configCache.fetchConfig().then((config) => sendMessage(MessageType.Owned, userSettings, config.system && config.system.welcomeOwner || '',
        '👋 Welcome to Tarlings', userSettings.getSystemUser() as IUserRecord, [userSettings.getControl()]));
}

export const sendWelcomeMemberMessage = (userSettings: StatefulSettings) => {
    void configCache.fetchConfig().then((config) => sendMessage(MessageType.Owned, userSettings, config.system && config.system.welcomeMember || '',
        '👋 Welcome to Tarlings', userSettings.getSystemUser() as IUserRecord, [userSettings.getControl()]));
}

export const sendEditOwnerProfileMessage = (userSettings: StatefulSettings) => {
    void configCache.fetchConfig().then((config) => sendMessage(MessageType.Owned, userSettings, config.system && config.system.ownerEditProfile || '',
        '👑 Todo: Update Owner Profile', userSettings.getSystemUser() as IUserRecord, [userSettings.getControl()]));
}